import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import BrandMark from "@/components/BrandMark";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!token) setErr("This link is missing a token. Please request a new reset email.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw !== pw2) {
      setErr("Passwords don't match.");
      return;
    }
    if (pw.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/enter", { replace: true }), 2500);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Something went wrong. Try requesting a new link.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-2 rounded-none border-0 border-b border-charcoal/40 bg-transparent focus-visible:ring-0 focus-visible:border-charcoal px-0 h-11";

  return (
    <div className="min-h-screen grid lg:grid-cols-[1fr_1.1fr] bg-bone">
      <div className="relative hidden lg:flex flex-col justify-between p-14 bg-charcoal text-bone">
        <BrandMark variant="light" size="lg" asLink={false} />
        <div className="max-w-md">
          <p className="text-[13px] uppercase tracking-[0.28em] text-bone/85 mb-6">Members · New password</p>
          <h1 className="font-serif text-6xl xl:text-7xl leading-[0.95] tracking-tight">
            Fresh start.<br/>
            <span className="italic text-bone/85">Set a new one.</span>
          </h1>
          <div className="h-px bg-bone/20 my-8" />
          <p className="text-sm text-bone/85 leading-relaxed max-w-sm">
            Choose a new password. This link is one-time-use.
          </p>
        </div>
        <p className="text-[12px] font-mono uppercase tracking-[0.25em] text-bone/85">
          Research use only · Not for human consumption
        </p>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:p-20">
        <div className="w-full max-w-md animate-fade-up" data-testid="reset-password-page">
          <p className="text-[13px] uppercase tracking-[0.28em] text-stone mb-6">Reset password</p>
          <h2 className="font-serif text-5xl tracking-tight mb-3">Choose a new password.</h2>
          <p className="text-sm text-stone mb-12">Minimum 6 characters. You'll be signed out of any other sessions.</p>

          {done ? (
            <div className="border border-borderSubtle bg-white p-6" data-testid="reset-password-done">
              <p className="overline text-oxblood mb-2">Updated</p>
              <p className="text-sm">Your password is set. Sending you to sign in…</p>
              <Link to="/enter" className="mt-4 inline-block text-[13px] uppercase tracking-[0.22em] text-oxblood hover:text-charcoal">
                Go now →
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="reset-password-form">
              <div>
                <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">New password</Label>
                <PasswordInput
                  value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6}
                  data-testid="reset-pw-input" className={inputCls}
                />
              </div>
              <div>
                <Label className="text-[13px] uppercase tracking-[0.22em] text-stone">Confirm password</Label>
                <PasswordInput
                  value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={6}
                  data-testid="reset-pw2-input" className={inputCls}
                />
              </div>
              {err && <p className="text-sm text-oxblood" data-testid="reset-error">{err}</p>}
              <button
                type="submit" disabled={busy || !token}
                className="w-full py-4 bg-charcoal text-bone tracking-[0.24em] text-xs uppercase hover:bg-oxblood transition-colors disabled:opacity-50 rounded-full"
                data-testid="reset-submit-btn"
              >
                {busy ? "Updating…" : "Set new password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
